import subscriptionDto from 'grpc-bob/subscription_pb';
import {Country, PaymentMethod} from 'grpc-bob/enum_pb';

import {SubscriptionClient, getMetadata} from './base';
import {PackageWithPromotion} from '../state-management/app';
import {getStudentIdFromToken} from '../utils';

const SubscriptionService = {
  async retrievePackages(planId: string, token: string) {
    const req = new subscriptionDto.RetrievePackagesRequest();

    req.setPlanId(planId);

    const resp = await SubscriptionClient.retrievePackages(req, getMetadata(token));

    return resp.toObject().packagesList
  },
  async retrievePlans(token: string) {
    const req = new subscriptionDto.RetrievePlansRequest();

    req.setCountry(Country.COUNTRY_VN); // currently payment only on VN

    const resp = await SubscriptionClient.retrievePlans(req, getMetadata(token));

    return resp.toObject().plansList
  },
  async getSubscriptionOrder(packageId: number, userId: string, promotion: string | undefined, token: string) {
    const req = new subscriptionDto.CreateOnlineOrderRequest();

    req.setPackageId(packageId);
    req.setStudentId(userId);

    if (promotion) {
      req.setPromotionCode(promotion);
    }
    const resp = await SubscriptionClient.createOnlineOrder(req, getMetadata(token));

    return resp.toObject();
  },
  async retrieveListOrder(token: string) {
    const idFromToken = getStudentIdFromToken(token);
    if (!idFromToken) throw new Error('cannotParseToken');

    const req = new subscriptionDto.ListOrdersRequest();

    req.setStudentIdsList([idFromToken!]);

    const resp = await SubscriptionClient.listOrders(req, getMetadata(token));
    return resp.toObject().ordersList
  },
  async checkPromotionUsage(code: string, country: Country, packageId: number, token: string) {
    const req = new subscriptionDto.CheckPromotionCodeRequest();

    req.setCode(code);
    req.setCountry(country);
    req.setPackageId(packageId);

    const resp = await SubscriptionClient.checkPromotionCode(req, getMetadata(token));
    const r = resp.toObject();
    if (r.promoDiscountAmount === 0 && r.amount === 0) {
      throw new Error('This code is invalid.');
    }
    return r
  },
  async createOrderCod({orderId, studentId, fullName, district, ward, packageId, promotionCode, phoneNumber, shippingAddress}: any, token: string) {
    const req = new subscriptionDto.CreateGHNOrderRequest();

    if (orderId) {
      req.setOrderId(orderId);
    }
    req.setPackageId(packageId);
    req.setStudentId(studentId);
    req.setCustomerName(fullName);
    req.setCustomerPhoneNumber(phoneNumber);
    req.setGhnDistrictId(district);
    req.setGhnWardCode(ward);
    req.setPromotionCode(promotionCode);
    req.setCustomerAddress(shippingAddress);

    const res = await SubscriptionClient.createGHNOrder(req, getMetadata(token));

    return res.toObject()
  },
  async applyPromoToPackage(packages: PackageWithPromotion[], promotionText: string, country: Country, token: string): Promise<PackageWithPromotion[]> {
    const appliedPromoPromises = packages.map(async (pack) => {
      const promo = await SubscriptionService.checkPromotionUsage(
        promotionText,
        country,
        pack.id,
        token
      );

      if (promo.amount === 0 && promo.promoDiscountAmount === 0) {
        return {
          ...pack,
          isPromotionApplied: false,
        }
      }

      return {
        ...pack,
        isPromotionApplied: true,
        promotionDiscountValue: promo.promoDiscountAmount,
        promoFinalPrice: promo.amount,
        expiryDate: promo.expiredDate.seconds
      }
    });
    return Promise.all(appliedPromoPromises);
  },
  async updatePaymentMethodForOrder(orderId: number, paymentMethod: PaymentMethod, token: string) {
    const req = new subscriptionDto.UpdatePaymentMethodRequest();

    req.setOrderId(orderId);
    req.setPaymentMethod(paymentMethod);

    const resp = await SubscriptionClient.updatePaymentMethod(req, getMetadata(token));
    return resp.toObject()
  },
  async markOrderProcessing(orderId: string, token: string) {
    const req = new subscriptionDto.MarkOrderProcessingRequest();

    if (!Number(orderId)) {
      req.setRef(orderId)
    } else {
      req.setOrderId(Number(orderId));
    }

    const resp = await SubscriptionClient.markOrderProcessing(req, getMetadata(token));

    return resp.toObject();
  },
  async retrieveCarriedOverPromotion(token : string) {
    const req = new subscriptionDto.RetrieveCarriedOverPromotionCodeRequest();

    const resp = await SubscriptionClient.retrieveCarriedOverPromotionCode(req, getMetadata(token));

    return resp.toObject();
  }
};

export default SubscriptionService
